import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { formatRoute } from 'react-router-named-routes';
import { useNavigate, useParams } from 'react-router-dom';
import PlannerEntryCard from './planner_entry_card';
import PlannerDayHeader from './planner_day_header';
import PlannerNavigationBar from './planner_navigation_bar';
import {
  DAY_INDEX_LABELS,
  DAY_INDEX_SHORT_LABELS,
  calendarAddDays,
  currentViewWeekStartDateStr,
  dayIndexesWithStartDay,
  daysArrayIndex,
  dbWeekStartDateForViewWeekAndIndex,
  dbWeekStartDatesAndDayIndexesCoveringViewWeek,
  labelForFirstDayOfWeek,
  labelForLastDayOfWeek,
  newDbPlannerWeekWithDefaults,
  plannerEntriesIndexOfObject,
  plannerEntryType,
  plannerWeekWithMealAdded,
  plannerWeekWithMealMoved,
  plannerWeekWithNoteAdded,
  plannerWeekWithObjectRemoved,
} from '../../services/planner';
import MealDetailContainer from '../meal/meal_detail_container';
import { scrollNewCardIntoView } from '../../services/smorg_board';
import { usePlannerViewWeekStartDate } from './planner_hooks';
import TopNavBarMobilePlanner from './top_nav_bar_mobile_planner';
import NewPlannerEntryForm from './new_planner_entry_form';
import NoteDetailContainer from './note_detail_container';
import Board from '../common/board';
import usePrevious from '../../services/use_previous';
import AddPlannerCardLink from './add_planner_card_link';
import MobileBoardLaneNavigator from '../common/mobile_board_lane_navigator';
import MealBasketWidget from '../../features/end_user_shopping/components/meal_basket_widget';
import {
  sumNutrition,
  targetDayNutritionFromMetricConstraints,
} from '../../services/nutrition';
import { deduplicate } from '../../services/arrays';
import { PlannerEntryType } from '../../API';
import { useHasBottomNav, useIsMobile } from '../common/layout_hooks';
import { MY_DAY_CONTENT_ENTRY_ROUTE } from '../../services/routes';
import {
  useCreateDbPlannerWeekMutation,
  useCreateMealMutation,
  useCreateNoteMutation,
  useDeleteContentEntryMutation,
  useDeleteMealMutation,
  useDeleteNoteMutation,
  useGetContentEntriesByParentQuery,
  useGetDbPlannerWeekQuery,
  useGetDistilledMealsByParentQuery,
  useGetNotesByParentQuery,
  useGetSharedProgrammeQuery,
  useLazyGetFullMealsByParentQuery,
  useLazyGetFullMealQuery,
  useLazyGetNotesByParentQuery,
  useUpdateDbPlannerWeekOptimisticallyMutation,
  useUpdateMealMutation,
  useUpdateNoteMutation,
  appSyncGraphQLApi,
} from '../../app/smorgAppSyncGraphQLApi';
import { targetCaloriesFromOnboardingAnswers } from '../../services/space_onboarding';
import ContentEntryDetailContainer from '../programmes/content_entry_detail_container';
import { mealsRemovedFromMealBasket } from '../../features/end_user_shopping/end_user_shopping_slice';
import {
  newNoteWithDefaultFields,
  noteWithParentUpdated,
} from '../../services/notes';
import { useTracker } from '../../operations/tracking_operations';
import { switchToViewWeek } from '../../features/end_user_planner/end_user_planner_slice';
import {
  CURRENT_MEAL_SCHEMA_VERSION,
  mealWithParentUpdated,
} from '../../services/meals';
import { updateFoodBrainDerivedDataOnMeal } from '../../operations/meal_operations';
import PlannerLoadErrorModal from './planner_load_error_modal';
import StaleScreenModal from '../common/stale_screen_modal';
import {
  areResourcesStaleSelector,
  StaleResourceType,
} from '../../features/stale_resource_detection/stale_resource_detection_slice';
import ResourceStatusControl from '../common/resource_status_control';
import {
  copyMealToPlanner,
  getOrCreatePlannerWeeks,
} from '../../operations/planner_operations';
import { customerFoodDataIDOfSpace } from '../../services/spaces';
import {
  useCurrentProgrammeEnrollmentQuery,
  useGetCurrentSpaceMembershipQuery,
  useGetUserProfileQuery,
  useLazyGetOrCreateDbPlannerWeekByStartDateQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import ResourceLoadingModal from '../common/resource_loading_modal';
import { SHARED_PROGRAMMES_REFRESH_INTERVAL_SECONDS } from '../../services/end_user_published_resources';
// import ContentEntryDetailContainer from '../programmes/content_entry_detail_container';

const boardCss = { textAlign: 'left' };

const boardComponents = {
  AddCardLink: AddPlannerCardLink,
  NewCardForm: NewPlannerEntryForm,
  Card: PlannerEntryCard,
  LaneHeader: PlannerDayHeader,
};

const PlannerView = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { data: userProfile } = useGetUserProfileQuery();

  const plannerViewWeekStartDate =
    usePlannerViewWeekStartDate() ||
    currentViewWeekStartDateStr(
      1 /* plannerViewWeekStartDayOrDefault(userProfile?.plannerViewWeekStartDay) */,
    );

  console.log({ plannerViewWeekStartDate });

  const startDatesAndDayIndexes = useMemo(
    () =>
      dbWeekStartDatesAndDayIndexesCoveringViewWeek(plannerViewWeekStartDate),
    [plannerViewWeekStartDate],
  );
  console.log(JSON.stringify(startDatesAndDayIndexes));

  const {
    data: plannerWeek,
    error: plannerWeekLoadError,
    isLoading: plannerWeekLoading,
  } = useGetDbPlannerWeekQuery(startDatesAndDayIndexes[0].dbWeekStartDate, {
    skip: !startDatesAndDayIndexes[0].dbWeekStartDate,
  });

  const {
    data: distilledMeals,
    error: distilledMealsLoadError,
    isLoading: distilledMealsLoading,
  } = useGetDistilledMealsByParentQuery(plannerWeek?.id, {
    skip: !plannerWeek?.id,
  });

  const {
    data: notes,
    error: notesLoadError,
    isLoading: notesLoading,
  } = useGetNotesByParentQuery(plannerWeek?.id, {
    skip: !plannerWeek?.id,
  });

  const {
    data: contentEntries,
    error: contentEntriesLoadError,
    isLoading: contentEntriesLoading,
  } = useGetContentEntriesByParentQuery(plannerWeek?.id, {
    skip: !plannerWeek?.id,
  });

  const {
    data: currentProgrammeEnrollment,
    error: enrollmentLoadError,
    isLoading: enrollmentLoading,
  } = useCurrentProgrammeEnrollmentQuery();

  const {
    data: sharedProgramme,
    error: sharedProgrammeLoadError,
    isLoading: sharedProgrammeLoading,
  } = useGetSharedProgrammeQuery(
    currentProgrammeEnrollment?.sharedProgrammeID,
    {
      skip: !currentProgrammeEnrollment?.sharedProgrammeID,
      refetchOnMountOrArgChange: SHARED_PROGRAMMES_REFRESH_INTERVAL_SECONDS,
    },
  );

  const {
    data: currentSpaceMembership,
    error: spaceMembershipLoadError,
    isLoading: spaceMembershipLoading,
  } = /* { data: null, error: null, isLoading: false }; */ useGetCurrentSpaceMembershipQuery();

  const customerFoodDataID = customerFoodDataIDOfSpace(
    currentSpaceMembership?.spaceID,
  );

  const [deleteMeal] = useDeleteMealMutation();

  const [createNote] = useCreateNoteMutation();

  const [createDbPlannerWeek] = useCreateDbPlannerWeekMutation();

  const [updateDbPlannerWeek] = useUpdateDbPlannerWeekOptimisticallyMutation();

  const [updateNote] = useUpdateNoteMutation();

  const [deleteNote] = useDeleteNoteMutation();

  const [deleteContentEntry] = useDeleteContentEntryMutation();

  const isLoaded =
    !plannerWeekLoading &&
    !distilledMealsLoading &&
    !notesLoading &&
    !contentEntriesLoading &&
    !enrollmentLoading &&
    !sharedProgrammeLoading &&
    !spaceMembershipLoading;

  const isLoading = !isLoaded;

  const loadError = [
    plannerWeekLoadError || '',
    distilledMealsLoadError || '',
    notesLoadError || '',
    contentEntriesLoadError || '',
    enrollmentLoadError || '',
    sharedProgrammeLoadError || '',
    spaceMembershipLoadError || '',
  ].join('');

  // console.log({ meals, notes, contentEntries });

  const isBlank =
    isLoaded &&
    Object.values(distilledMeals || {}).length === 0 &&
    Object.values(notes || {}).length === 0 &&
    Object.values(contentEntries || {}).length === 0;

  const plannerWeekDays = useMemo(
    () =>
      startDatesAndDayIndexes.map((sdi) => {
        if (plannerWeek?.startDate !== sdi.dbWeekStartDate) {
          return { dayIndex: sdi.dayIndex };
        }
        return {
          dayIndex: sdi.dayIndex,
          plannerDbWeekStartDate: sdi.dbWeekStartDate,
          parentBoardId: plannerWeek?.id,
          entries: plannerWeek?.days[daysArrayIndex(sdi.dayIndex)].entries,
        };
      }),
    [
      plannerWeek?.days,
      plannerWeek?.id,
      plannerWeek?.startDate,
      startDatesAndDayIndexes,
    ],
  );

  const userPlannerViewStartDay = 1; /* plannerViewWeekStartDayOrDefault(
    userProfile?.plannerViewWeekStartDay,
  ); */

  const dbPlannerWeekIDs = deduplicate(
    (plannerWeekDays || []).map((day) => day.parentBoardId),
  );

  const [lazyGetOrCreateDbPlannerWeekByStartDate] =
    useLazyGetOrCreateDbPlannerWeekByStartDateQuery();

  const [lazyGetNotesByParent] = useLazyGetNotesByParentQuery();

  useEffect(() => {
    if (!plannerWeek && !plannerWeekLoading && currentSpaceMembership?.id) {
      createDbPlannerWeek(
        newDbPlannerWeekWithDefaults(
          startDatesAndDayIndexes[0].dbWeekStartDate,
          currentSpaceMembership?.id,
        ),
      );
    }
  }, [
    createDbPlannerWeek,
    currentSpaceMembership?.id,
    plannerWeek,
    plannerWeekLoading,
    startDatesAndDayIndexes,
  ]);

  const isMobile = useIsMobile();

  const { mealId: objectId } = useParams();

  const detailVisibleFor = useMemo(() => {
    const dvf = {
      objectId: null,
      parentBoardId: null,
    };

    if (objectId && plannerWeekDays) {
      dvf.objectId = objectId;
      dvf.parentBoardId = plannerWeekDays.find(
        (day) => plannerEntriesIndexOfObject(day.entries, objectId) !== -1,
      ).parentBoardId;
    }
    dvf.plannerEntryType = plannerEntryType(
      [plannerWeek],
      plannerViewWeekStartDate,
      objectId,
    );
    return dvf;
  }, [objectId, plannerViewWeekStartDate, plannerWeek, plannerWeekDays]);

  const [createMeal] = useCreateMealMutation();

  const [updateMeal] = useUpdateMealMutation();

  const [lazyGetMeal] = useLazyGetFullMealQuery();

  const afterCardAdd = async (reactTrelloCard, laneId) => {
    console.log({ reactTrelloCard });
    const newMeal = reactTrelloCard.style._meal;
    const isImported = !!newMeal.recipes[0]?.recipeUrl;
    const shouldOpenMealDetail =
      !!reactTrelloCard.style?._should_open_meal_detail;
    if (newMeal) {
      const input = {
        ...newMeal,
        schemaVersion: CURRENT_MEAL_SCHEMA_VERSION,
        smorgBoardID: plannerWeek.id,
        spaceMembershipID: currentSpaceMembership.id,
      };
      const { data: meal } = await createMeal(input);
      const updatedPlannerWeek = plannerWeekWithMealAdded(
        plannerWeek,
        meal,
        laneId,
      );
      // console.log(JSON.stringify(updatedPlannerWeek));
      await updateDbPlannerWeek(updatedPlannerWeek);

      if (shouldOpenMealDetail) {
        navigate(`/planner/${plannerViewWeekStartDate}/meal/${meal.id}`);
      }

      const counters = ['numMealsAdded'];
      if (isImported) {
        counters.push('numMealsImported');
      } else {
        counters.push('numMealsAddedManually');
      }

      tracker(
        userProfile,
        [
          {
            name: 'Add new meal',
            args: {
              import: isImported,
              url: meal.recipes[0]?.recipeUrl,
              section: 'planner',
            },
          },
        ],
        counters,
      );
      await updateFoodBrainDerivedDataOnMeal(
        meal,
        customerFoodDataID,
        updateMeal,
      );
    }
    const searchResultMealID = reactTrelloCard.style._searchResult?.mealID;
    if (searchResultMealID) {
      const dayIndex = laneId;
      const { data: fullMeal } = await lazyGetMeal(searchResultMealID);
      copyMealToPlanner(
        fullMeal,
        plannerViewWeekStartDate,
        [dayIndex],
        customerFoodDataID,
        tracker,
        userProfile,
        currentSpaceMembership,
        lazyGetOrCreateDbPlannerWeekByStartDate,
        createMeal,
        updateMeal,
        updateDbPlannerWeek,
        lazyGetMeal,
      );
    }
    const newNote = reactTrelloCard.style._note;
    if (newNote) {
      const input = newNoteWithDefaultFields(
        newNote,
        plannerWeek.id,
        currentSpaceMembership.id,
        null,
      );
      const { data: insertedNote } = await createNote(input);
      const updatedPlannerWeek = plannerWeekWithNoteAdded(
        plannerWeek,
        insertedNote,
        laneId,
      );
      // console.log(JSON.stringify(updatedPlannerWeek));
      await updateDbPlannerWeek(updatedPlannerWeek);

      tracker(
        userProfile,
        [
          {
            name: 'Add new note',
            args: {
              note_title: newNote.title,
              section: 'planner',
            },
          },
        ],
        ['numNotesAdded'],
      );
      navigate(`/planner/${plannerViewWeekStartDate}/meal/${insertedNote.id}`);
    }
  };

  const prevDistilledMeals = usePrevious(distilledMeals);
  const prevNotes = usePrevious(notes);
  const prevContentEntries = usePrevious(contentEntries);

  useEffect(() => {
    scrollNewCardIntoView(distilledMeals, prevDistilledMeals);
  }, [distilledMeals, prevDistilledMeals]);

  useEffect(() => {
    scrollNewCardIntoView(notes, prevNotes);
  }, [notes, prevNotes]);

  useEffect(() => {
    scrollNewCardIntoView(contentEntries, prevContentEntries);
  }, [contentEntries, prevContentEntries]);

  const tracker = useTracker();

  const [lazyGetFullMealsByParent] = useLazyGetFullMealsByParentQuery();

  useEffect(() => {
    console.log(`useEffect tracking Planner clicked, tracker = ${tracker}`);
    tracker(userProfile, ['Planner clicked'], ['numPlansViewed']);
    // tracker() will mutate userProfile, causing a render loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plannerViewWeekStartDate, tracker, !!userProfile]);

  useEffect(() => {
    console.log(`useEffect tracking Switch to plannerweek`);
    dispatch(switchToViewWeek(plannerViewWeekStartDate));
  }, [dispatch, plannerViewWeekStartDate]);

  const onCardClick = (cardId, metadata, laneId) => {
    console.log({ cardId, metadata, laneId });
    if (contentEntries && contentEntries[cardId]) {
      navigate(
        formatRoute(MY_DAY_CONTENT_ENTRY_ROUTE, {
          parentID: plannerWeek?.id,
          sharedContentEntryID: cardId,
        }),
      );
    } else {
      navigate(`/planner/${plannerViewWeekStartDate}/meal/${cardId}`);
    }
  };

  const afterCardDrag = async (
    cardId,
    sourceLaneId,
    targetLaneId,
    position,
    /* cardDetails, */
  ) => {
    const sourceDayIndex = parseInt(sourceLaneId, 10);
    const targetDayIndex = parseInt(targetLaneId, 10);
    console.log({
      plannerViewWeekStartDate,
      objectID: cardId,
      sourceDayIndex,
      toPlannerViewWeekStartDate: plannerViewWeekStartDate,
      targetDayIndex,
      position,
    });
    // console.log({ sourcePlannerWeekDays, targetPlannerWeekDays });
    const sourceDbWeekStartDate = dbWeekStartDateForViewWeekAndIndex(
      plannerViewWeekStartDate,
      sourceDayIndex,
    );
    const targetDbWeekStartDate = dbWeekStartDateForViewWeekAndIndex(
      plannerViewWeekStartDate,
      targetDayIndex,
    );
    const dbWeekStartDates = deduplicate([
      sourceDbWeekStartDate,
      targetDbWeekStartDate,
    ]);
    const plannerWeeks = await getOrCreatePlannerWeeks(
      dbWeekStartDates,
      lazyGetOrCreateDbPlannerWeekByStartDate,
    );
    const sourcePlannerWeek = plannerWeeks[sourceDbWeekStartDate];
    const targetPlannerWeek = plannerWeeks[targetDbWeekStartDate];
    if (sourceDbWeekStartDate === targetDbWeekStartDate) {
      const updatedPlannerWeek = plannerWeekWithMealMoved(
        plannerWeek,
        cardId,
        sourceDayIndex,
        targetDayIndex,
        position,
      );
      updateDbPlannerWeek(updatedPlannerWeek);
    } else {
      const entryType = plannerEntryType(
        plannerWeeks,
        plannerViewWeekStartDate,
        cardId,
      );
      const { data: sourceWeekNotes } = await lazyGetNotesByParent(
        sourcePlannerWeek.id,
        true,
      );
      const { data: sourceWeekMeals } = await lazyGetFullMealsByParent(
        sourcePlannerWeek.id,
        true,
      );
      if (entryType === PlannerEntryType.NOTE) {
        const updatedNote = noteWithParentUpdated(
          sourceWeekNotes[cardId],
          plannerWeeks[targetDbWeekStartDate].id,
        );
        await updateNote(updatedNote);
      } else if (entryType === PlannerEntryType.CONTENT_ENTRY) {
        try {
          // dispatch({
          //   type: 'CONTENT_ENTRY_PARENT_CHANGED',
          //   objectID: cardId,
          //   parentID: plannerWeeks[targetDbWeekStartDate].id,
          // });
        } finally {
          // await dispatch(syncContentEntryAction(cardId));
        }
      } else {
        const updatedMeal = mealWithParentUpdated(
          sourceWeekMeals[cardId],
          plannerWeeks[targetDbWeekStartDate].id,
        );
        await updateMeal(updatedMeal);
      }
      const updatedSourcePlannerWeek = plannerWeekWithObjectRemoved(
        sourcePlannerWeek,
        cardId,
        sourceDayIndex,
      );
      const updatedTargetPlannerWeek =
        entryType === 'NOTE'
          ? plannerWeekWithNoteAdded(
              targetPlannerWeek,
              sourceWeekNotes[cardId],
              targetDayIndex,
              position,
            )
          : plannerWeekWithMealAdded(
              targetPlannerWeek,
              sourceWeekMeals[cardId],
              targetDayIndex,
              position,
            );
      await Promise.all([
        updateDbPlannerWeek(updatedSourcePlannerWeek),
        updateDbPlannerWeek(updatedTargetPlannerWeek),
      ]);
    }
    // Cancel the drop, because the board will re-render with the new structure.
    return false;
  };

  const currentlyEnrolledProgrammeShowsNutrition =
    !!sharedProgramme?.showNutritionToUsers;

  const { nutritionConstraints } = sharedProgramme || {};

  const preferredMetrics = useMemo(
    () =>
      deduplicate(
        (nutritionConstraints?.nutritionMetricConstraints || []).map(
          (c) => c.nutritionMetric,
        ),
      ).slice(0, 3),
    [nutritionConstraints?.nutritionMetricConstraints],
  );

  if (!preferredMetrics.includes('calories')) {
    preferredMetrics.unshift('calories');
  }

  // If the user's space membership field showNutritionHeader has a defined value,
  // always use that.
  const hasNutritionHeader =
    currentSpaceMembership?.showNutritionHeader === null ||
    currentSpaceMembership?.showNutritionHeader === undefined
      ? !!currentProgrammeEnrollment && currentlyEnrolledProgrammeShowsNutrition
      : currentSpaceMembership?.showNutritionHeader;

  const targetCalories =
    currentSpaceMembership?.customTargetCalories ||
    targetCaloriesFromOnboardingAnswers(
      currentSpaceMembership?.onboardingAnswers || [],
    );

  const reactTrelloData = useMemo(() => {
    console.log('Start computing reactTrelloData');
    return {
      lanes: plannerWeekDays.map((day, viewArrayIndex) => {
        const cards = (day.entries || [])
          .map((plannerEntry) => {
            if (plannerEntry.plannerEntryType === PlannerEntryType.NOTE) {
              const note = (notes || {})[plannerEntry.objectID];
              if (!note) {
                return null;
              }
              return {
                id: note.id,
                title: note.title,
                style: {
                  _parentID: day.parentBoardId,
                  _plannerEntryType: plannerEntry.plannerEntryType,
                },
              };
            }
            if (
              plannerEntry.plannerEntryType === PlannerEntryType.CONTENT_ENTRY
            ) {
              const contentEntry = (contentEntries || {})[
                plannerEntry.objectID
              ];
              if (!contentEntry) {
                console.warn(
                  `Content entry with ID ${plannerEntry.objectID} not found`,
                );
                return null;
              }
              return {
                id: contentEntry.id,
                title: contentEntry.title,
                style: {
                  _parentID: day.parentBoardId,
                  _plannerEntryType: plannerEntry.plannerEntryType,
                  _dayIndex: day.dayIndex,
                  _objectID: plannerEntry.objectID,
                },
              };
            }
            const distilledMeal = (distilledMeals || {})[plannerEntry.mealID];
            if (!distilledMeal) {
              return null;
            }
            return {
              id: distilledMeal.id,
              title: distilledMeal.recipes[0].title,
              style: {
                _calendarDay: calendarAddDays(
                  plannerViewWeekStartDate,
                  viewArrayIndex,
                ),
                _parentID: day.parentBoardId,
                _dayIndex: day.dayIndex,
                _mealType: ((distilledMeal?.recipes || [])[0]?.mealTypes ||
                  [])[0],
              },
              description: distilledMeal.recipes[0].shortDescription || '',
            };
          })
          .filter((card) => !!card);

        const mealIDs = (day.entries || [])
          .filter(
            (plannerEntry) =>
              plannerEntry.plannerEntryType === null ||
              plannerEntry.plannerEntryType === PlannerEntryType.MEAL,
          )
          .map((plannerEntry) => plannerEntry.mealID);

        // console.log({ mealIDs });

        // console.log(mealIDs.map((mealID) => meals[mealID]));

        const mealDayNutrition = mealIDs.reduce(
          (acc, mealID) =>
            sumNutrition(
              acc,
              (distilledMeals || {})[mealID]?.derivedNutrition
                ?.totalNutritionPerServing || {},
            ),
          {},
        );

        const actualTotalNutrition = mealDayNutrition;

        console.log({ actualTotalNutrition });

        const preferredMetricsWithRules = preferredMetrics.map(
          (metricName) => ({
            metricName,
            rules:
              [] /* (nutritionConstraints?.nutritionMetricConstraints || [])
              .filter((c) => c.nutritionMetric === metricName)
              .map((c) => ({
                operator: c.operator,
                value: c.value,
                units: c.units,
              })), */,
          }),
        );

        const targetNutrition = targetDayNutritionFromMetricConstraints(
          nutritionConstraints?.nutritionMetricConstraints || [],
          preferredMetrics,
          targetCalories,
        );

        return {
          id: day.dayIndex.toString(),
          title: DAY_INDEX_LABELS[day.dayIndex],
          titleStyle: hasNutritionHeader && {
            _nutritionHeader: {
              targetNutrition,
              actualTotalNutrition,
              preferredMetricsWithRules,
              targetCalories,
            },
          },
          cards,
        };
      }),
    };
  }, [
    plannerWeekDays,
    preferredMetrics,
    nutritionConstraints?.nutritionMetricConstraints,
    targetCalories,
    hasNutritionHeader,
    distilledMeals,
    plannerViewWeekStartDate,
    notes,
    contentEntries,
  ]);

  console.log({ reactTrelloData });

  const prevPlannerViewWeekStartDate = usePrevious(plannerViewWeekStartDate);

  const { hash } = window.location;
  const hashDayIndex = hash && hash.substring(1);
  const hashDayIndexSelector =
    hashDayIndex && `section[id="lane-${hashDayIndex}"]`;
  // const dayElExists = !!(
  //   hashDayIndexSelector && document.querySelector(hashDayIndexSelector)
  // );

  useEffect(() => {
    if (hashDayIndex) {
      console.log(
        `useEffect Hash param says scroll day ${hashDayIndex} into view.`,
      );
      window.setTimeout(() => {
        const dayEl = document.querySelector(hashDayIndexSelector);
        if (dayEl) {
          dayEl.scrollIntoView();
        }
      }, 10);
      return;
    }
    if (prevPlannerViewWeekStartDate) {
      if (prevPlannerViewWeekStartDate < plannerViewWeekStartDate) {
        // User navigated to next week
        const sectionTitleFirstDayOfViewWeek = labelForFirstDayOfWeek(
          userPlannerViewStartDay,
        );
        const selector = `section[title="${sectionTitleFirstDayOfViewWeek}"]`;
        window.setTimeout(() => {
          const firstDayEl = document.querySelector(selector);
          if (firstDayEl) {
            firstDayEl.scrollIntoView();
          }
        }, 10);
      } else if (prevPlannerViewWeekStartDate > plannerViewWeekStartDate) {
        // User navigated to previous week
        const sectionTitleLastDayOfViewWeek = labelForLastDayOfWeek(
          userPlannerViewStartDay,
        );
        window.setTimeout(() => {
          const lastDayEl = document.querySelector(
            `section[title="${sectionTitleLastDayOfViewWeek}"]`,
          );
          if (lastDayEl) {
            lastDayEl.scrollIntoView();
          }
        }, 10);
      }
    }
  }, [
    prevPlannerViewWeekStartDate,
    plannerViewWeekStartDate,
    userPlannerViewStartDay,
    hashDayIndex,
    hashDayIndexSelector,
  ]);

  const hasBottomNav = useHasBottomNav();

  const boardScrollableRef = useRef();

  const allDayIndexes = dayIndexesWithStartDay(userPlannerViewStartDay);

  let navHeightWithBottomNav = 54 + 35 + 10 + 69;
  const navHeightWithTopNav = 59 + 34;
  // const addNewFormHeight = 74;

  // const addNewFormEnabled = false;

  if (!isMobile && hasBottomNav) {
    navHeightWithBottomNav -= 35;
  }

  const heightRuleWithBottomNav = {
    height: `calc(var(--app-height) - ${navHeightWithBottomNav}px)`,
  };
  const heightRuleWithTopNav = {
    height: `calc(var(--app-height) - ${navHeightWithTopNav}px)`,
  };
  const boardCssWithBottomNav = { ...boardCss, ...heightRuleWithBottomNav };
  const boardCssWithTopNav = { ...boardCss, ...heightRuleWithTopNav };

  let freeHeightWithBottomNav = navHeightWithBottomNav;
  let freeHeightWithTopNav = navHeightWithTopNav;

  if (hasNutritionHeader) {
    freeHeightWithBottomNav += 120;
    freeHeightWithTopNav += 120;
  }

  if (hasBottomNav) {
    document.documentElement.style.setProperty(
      '--planner-free-height',
      `${freeHeightWithBottomNav + 25}px`,
    );
  } else {
    document.documentElement.style.setProperty(
      '--planner-free-height',
      `${freeHeightWithTopNav}px`,
    );
  }

  const laneIDs = reactTrelloData.lanes.map((l) => l.id);

  const onVisibleLaneChange = useCallback((laneID) => {
    console.log(`Visible lane changed to ${laneID}`);
    window.history.replaceState(undefined, undefined, `#${laneID}`);
  }, []);
  console.log('Rendering PlannerView');

  const style = useMemo(
    () => (hasBottomNav ? boardCssWithBottomNav : boardCssWithTopNav),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      // eslint-disable-next-line react-hooks/exhaustive-deps
      JSON.stringify(boardCssWithBottomNav),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      JSON.stringify(boardCssWithTopNav),
      hasBottomNav,
    ],
  );
  const backdropStyle = useMemo(
    () => (hasBottomNav ? heightRuleWithBottomNav : heightRuleWithTopNav),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      hasBottomNav,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      JSON.stringify(heightRuleWithBottomNav),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      JSON.stringify(heightRuleWithTopNav),
    ],
  );

  const onDeletePlannerEntry = useCallback(
    async (objectIdToDelete, laneId) => {
      const dayIndex = laneId;
      const entryType = plannerEntryType(
        [plannerWeek],
        plannerViewWeekStartDate,
        objectIdToDelete,
      );
      if (entryType === PlannerEntryType.NOTE) {
        await deleteNote({ id: objectIdToDelete, parentID: plannerWeek.id });
      } else if (entryType === PlannerEntryType.CONTENT_ENTRY) {
        await deleteContentEntry({
          id: objectIdToDelete,
          parentID: plannerWeek.id,
        });
      } else {
        dispatch(mealsRemovedFromMealBasket([objectIdToDelete]));
        const updatedDbPlannerWeek = plannerWeekWithObjectRemoved(
          plannerWeek,
          objectIdToDelete,
          dayIndex,
        );
        await Promise.all([
          deleteMeal({
            id: objectIdToDelete,
            parentID: plannerWeek.id,
          }),
          updateDbPlannerWeek(updatedDbPlannerWeek),
        ]);
      }
    },
    [
      deleteContentEntry,
      deleteMeal,
      deleteNote,
      dispatch,
      plannerViewWeekStartDate,
      plannerWeek,
      updateDbPlannerWeek,
    ],
  );

  const onDismissLoadErrorModal = () => {
    // TODO
    // dispatch({
    //   type: 'PLANNER_NETWORK_STATE_CHANGED',
    //   networkState: { loading: false },
    // });
  };

  const isPlannerViewStale = useSelector((state) => {
    return areResourcesStaleSelector(state, StaleResourceType.PlannerWeek, [
      plannerWeek,
    ]);
  });

  const onReloadStaleData = () => {
    dispatch(
      appSyncGraphQLApi.util.invalidateTags([
        { type: 'DbPlannerWeekByStartDate', id: plannerWeek.startDate },
        { type: 'DbPlannerWeekByID', id: plannerWeek.id },
      ]),
    );
  };

  return (
    <>
      <PlannerNavigationBar
        plannerViewWeekStartDate={plannerViewWeekStartDate}
      />
      <TopNavBarMobilePlanner
        plannerViewWeekStartDate={plannerViewWeekStartDate}
      />
      <MealBasketWidget />
      <div className="smorg-board-container" id="planner-board">
        {isMobile && hasBottomNav && (
          <MobileBoardLaneNavigator
            boardScrollableRef={boardScrollableRef}
            laneIDs={laneIDs}
            initialActiveLaneID={hashDayIndex}
            renderItem={(itemIndexZeroBased, isSelected, onClick) => (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 'bold',
                  margin: '8px',
                  textDecoration: isSelected ? 'underline' : 'none',
                }}
                onClick={onClick}>
                {DAY_INDEX_SHORT_LABELS[allDayIndexes[itemIndexZeroBased]]}
              </div>
            )}
            onVisibleLaneChange={onVisibleLaneChange}
          />
        )}
        <Board
          editable
          data={reactTrelloData}
          components={boardComponents}
          onCardAdd={useCallback(afterCardAdd, [
            createMeal,
            createNote,
            currentSpaceMembership,
            customerFoodDataID,
            lazyGetOrCreateDbPlannerWeekByStartDate,
            lazyGetMeal,
            navigate,
            plannerViewWeekStartDate,
            plannerWeek,
            tracker,
            updateDbPlannerWeek,
            updateMeal,
            userProfile,
          ])}
          onCardClick={useCallback(onCardClick, [
            contentEntries,
            navigate,
            plannerViewWeekStartDate,
            plannerWeek?.id,
          ])}
          onCardDelete={onDeletePlannerEntry}
          handleDragEnd={useCallback(afterCardDrag, [
            lazyGetOrCreateDbPlannerWeekByStartDate,
            lazyGetFullMealsByParent,
            lazyGetNotesByParent,
            plannerViewWeekStartDate,
            plannerWeek,
            updateDbPlannerWeek,
            updateMeal,
            updateNote,
          ])}
          boardScrollableRef={boardScrollableRef}
          backdropContent={
            !hasBottomNav &&
            isBlank && (
              <>
                Stuck? Want to know how to plan your first meals?
                <br />
                <br />
                <a
                  style={{ color: 'var(--app-primary-font-color)' }}
                  href="https://help.mealiq.io/programs/fill-your-program-with-recipes"
                  target="_blank"
                  rel="noreferrer">
                  Yes please! I need some help
                </a>
              </>
            )
          }
          style={style}
          backdropStyle={backdropStyle}
        />
      </div>
      {detailVisibleFor.plannerEntryType === PlannerEntryType.NOTE && (
        <NoteDetailContainer
          parentSmorgBoardId={detailVisibleFor.parentBoardId}
          noteId={detailVisibleFor.objectId}
          visible={!!plannerWeekDays && !!detailVisibleFor.objectId}
          dismiss={() => navigate(-1)}
        />
      )}
      {detailVisibleFor.plannerEntryType === PlannerEntryType.CONTENT_ENTRY && (
        <ContentEntryDetailContainer
          parentSmorgBoardId={detailVisibleFor.parentBoardId}
          contentEntryID={detailVisibleFor.objectId}
          visible={!!plannerWeekDays && !!detailVisibleFor.objectId}
          dismiss={() => navigate(-1)}
        />
      )}
      {(detailVisibleFor.plannerEntryType === PlannerEntryType.MEAL ||
        !detailVisibleFor.plannerEntryType) &&
        detailVisibleFor.objectId && (
          <MealDetailContainer
            parentSmorgBoardId={detailVisibleFor.parentBoardId}
            menuId={null}
            mealId={detailVisibleFor.objectId}
            visible={!!plannerWeekDays && !!detailVisibleFor.objectId}
            dismiss={() => navigate(-1)}
          />
        )}
      {isLoading && <ResourceLoadingModal />}
      {!isLoading && !!loadError && (
        <PlannerLoadErrorModal
          loadError={loadError}
          onDismiss={onDismissLoadErrorModal}
          // onRetry={onRetryLoadAfterError}
        />
      )}
      {isPlannerViewStale && <StaleScreenModal onSync={onReloadStaleData} />}
      <ResourceStatusControl
        resourceType={StaleResourceType.PlannerWeek}
        resourceIDs={dbPlannerWeekIDs}
      />
    </>
  );
};

export default PlannerView;
